import '../../style/templates/theme.scss';
import { ProductSliders, ProductGrids } from '@modules/mountProductCards';
import PrimaryHeroModule from '@modules/PrimaryHero';
import SpotlightModule from '@modules/Spotlight';
import { CartApp } from '../layout/mountCart';
import CategoryModule from '@modules/Category';
import Navigation from '@modules/navigation';
import StaticNavigation from '@modules/StaticNavigation';
import UrlProps from '@modules/urlProps';
import Cart from '@modules/Cart';
import '@modules/mountSearch';
import Nanobar from '@modules/nanobar';
import '@modules/StaticNanobar';
import SeoModule from '@modules/seo';
import StoryModule from '@modules/story';
import VisualCategory from '@modules/VisualCategory';
import SecondaryHeroModule from '@modules/story';
import ValuePropsModule from '@modules/valueProps';
import EmailCapture from '@utils/emailCapture';
import { pubSub } from '@utils/PubSub';
import renderMultipleMetaobjects from '@utils/Metaobjects';
import {
  emailValidate,
  generatePreviewLinks,
  initCustomAsset,
  initCustomModalTriggers,
  renderModules,
  updateBtnClickArea,
  sendDYAudiences,
  sendDYPromoObject,
} from '@utils/global-functions';
import PartialOverlay from '@utils/PartialOverlay';
import addOverlayListeners from '@utils/overlay';
import SplitOverlay from '@utils/splitOverlay';
import ExitOverlay from '@utils/exitOverlay';
import Slider from '@utils/slider';
import { initCustomLinebreaks } from '@utils/breakpoints';
import '@utils/accordion';
import { urlCookies } from '@utils/urlCookies';
import { getUserPlatform } from '@utils/getUserOS';
import Breadcrumbs from '@components/breadcrumbs';
import CipaBanner from '@modules/cipa';
import '@modules/Reviews';
import PromoBanner from '@modules/PromoBanner';
import InPageNavigation from '@modules/inPageNavigation';
import VisualValue from '@modules/visualValue';
import LifestyleTileGrid from '@modules/lifestyleTileGrid';
import SplitHero from '@modules/SplitHero';
import AccountDropdown from '@components/account-dropdown';
import { fetchLoyaltyDetails } from '@utils/loyaltyDetails';
import { useSwym } from '@utils/Swym';
import { SwymButton } from '@components/SwymButton';
import { WishlistNotification } from '@components/SwymNotification';

// Add in img and svg assets to the story module ( or anywhere )
async function awaitAssets() {
  await Promise.all([initCustomLinebreaks(), initCustomAsset()]);
}
//set DY promo object
async function setPromoObject() {
  const promoObject = window.Brooklinen.promo.discounts;
  if (promoObject) return promoObject;
}

awaitAssets();

const modules = [
  {
    //https://brooklinen.atlassian.net/wiki/spaces/BO2/pages/1820098587/SEO+Module
    component: (context) => new SeoModule(context),
    context: [...document.querySelectorAll('seo-module')],
  },
  {
    //https://brooklinen.atlassian.net/wiki/spaces/BO2/pages/1793851393/Cart
    component: () => new Cart(),
  },
  {
    //https://brooklinen.atlassian.net/wiki/spaces/BO2/pages/1848934402/Nanobar
    component: (context) => new Nanobar(context),
    context: [...document.querySelectorAll('nano-bar')],
  },
  {
    //CIPA Cookie Banner
    component: (context) => new CipaBanner(context),
    context: [...document.querySelectorAll('.cipa-banner')],
  },
  {
    //https://brooklinen.atlassian.net/wiki/spaces/BO2/pages/1849163777/Navigation
    component: () =>
      document.querySelector('[data-static-nav]')
        ? new StaticNavigation()
        : new Navigation(),
  },
  {
    //Product Slider (vue)
    component: (context) => new ProductSliders(context),
    context: [...document.querySelectorAll('product-slider')],
  },
  {
    //Product Grid (vue)
    component: (context) => new ProductGrids(context),
    context: [...document.querySelectorAll('product-grid')],
  },
  {
    //URL Props (for ometria)
    component: () => new UrlProps(),
  },
  {
    //Partial Overlay
    component: (context) => new PartialOverlay(context),
    context: [...document.querySelectorAll('#overlayPartial')],
  },
  {
    //https://brooklinen.atlassian.net/wiki/spaces/BO2/pages/1846214662/Spotlight+Module
    component: (context) => new SpotlightModule(context),
    context: [...document.querySelectorAll('.js-spotlight-module')],
  },
  {
    //Category Module
    component: (context) => new CategoryModule(context),
    context: [...document.querySelectorAll('.js-category-module')],
  },
  {
    //Breadcrumbs
    component: () => {
      if (document.querySelector('.breadcrumbs')) {
        const breadcrumbs = new Breadcrumbs();
        breadcrumbs.init();
      }
    },
  },
  {
    //Main Cart
    component: (context) => new CartApp(context),
    context: [...document.querySelectorAll('main-cart')],
  },
  {
    //Entrance Overlay
    component: (context) => {
      const initOverlay = () => {
        const windowOverlay = window.Brooklinen.EntranceOverlay,
          delay = windowOverlay.general.overlay_delay,
          isDelayANumber = isFinite(delay),
          delayTime = isDelayANumber ? delay : 0;
        setTimeout(() => {
          new SplitOverlay(context);
          pubSub.unsubscribe('dy:overlay:steps:init');
          document.cookie = '_dy_entrance_overlay_shown=true; path=/';
        }, delayTime);
      };
      if (document.cookie.indexOf('_dy_entrance_overlay_shown') == -1)
        pubSub.subscribe('dy:overlay:steps:init', initOverlay);
    },
    context: [...document.querySelectorAll('#splitOverlay')],
  },
  {
    //Slider functionality for existing modules
    component: (context) => new Slider(context),
    context: [...document.querySelectorAll('.js-splide-slider')],
  },
  {
    //Email Capture
    component: (context) => new EmailCapture(context),
    context: [...document.querySelectorAll('.component--email-capture')],
  },
  {
    //Promo Banner Module
    component: (context) => new PromoBanner(context),
    context: [...document.querySelectorAll('.js-promo-banner')],
  },
  {
    //Exit Intent
    component: (context) => new ExitOverlay(context),
    context: [...document.querySelectorAll('.js-exit-overlay')],
  },
  {
    //Story Module
    component: (context) => new StoryModule(context),
    context: [...document.querySelectorAll('.section-storymodule')],
  },
  {
    //Secondary Hero Module
    component: (context) => new SecondaryHeroModule(context),
    context: [...document.querySelectorAll('.section-secondary-hero')],
  },
  {
    //Value Props Module
    component: (context) => new ValuePropsModule(context),
    context: [...document.querySelectorAll('.section-value-props')],
  },
  {
    //In Page Navigation
    component: (context) => new InPageNavigation(context),
    context: [...document.querySelectorAll('.section-in-page-navigation')],
  },
  {
    //Visual Value Prop
    component: (context) => new VisualValue(context),
    context: [...document.querySelectorAll('.visual-value-prop')],
  },
  {
    //Visual Category
    component: (context) => new VisualCategory(context),
    context: [...document.querySelectorAll('.js-visual-category-module')],
  },
  {
    //Lifestyle Tile Grid
    component: (context) => new LifestyleTileGrid(context),
    context: [...document.querySelectorAll('.section-lifestyle-tile-grid')],
  },
  {
    //Split Hero Module
    component: (context) => new SplitHero(context),
    context: [...document.querySelectorAll('.js-split-hero-module')],
  },
  {
    // Account Dropdown
    component: () => new AccountDropdown(),
  },
  {
    // Swym Button
    component: () => new SwymButton(),
  },
  {
    component: () => new WishlistNotification(),
  },
];

function initializeJS() {
  //triggers window.pubSubReady for DY 2.0 campaigns to initialize
  pubSub.broadcast();

  const primaryHeroModule = Array.from(
    document.querySelectorAll('.js-primary-hero-module')
  );
  primaryHeroModule.forEach((module) => new PrimaryHeroModule(module));

  //enables blCookieSet and blCookieRemove URL param functionality
  urlCookies.init();

  //gets current user's OS and browser platform
  getUserPlatform.init();

  //renders and initializes class component modules
  renderModules(modules);

  //adds custom functionality to CTAs if needed (i.e. Gladly chat widget)
  initCustomModalTriggers();

  //initializes sitewide modal functionality
  addOverlayListeners();

  //allows for multiple metaobject references to be used in cases with more than one instance of a module (i.e. story on PDP)
  renderMultipleMetaobjects();

  //Set promo DY object
  pubSub.subscribe('dy:promo:init', setPromoObject, setPromoObject);

  // match input elements with the data-validate property
  const inputs = document.querySelectorAll('input[data-validate]');

  Array.from(inputs)?.forEach((input) => {
    input.addEventListener('blur', () => {
      emailValidate(input);
    });
  });

  updateBtnClickArea();

  sendDYAudiences();
  sendDYPromoObject();

  generatePreviewLinks();

  useSwym.init();

  fetchLoyaltyDetails();
}

//initialize JS when either the DOM content loads, or when you are previewing the site in the Theme Customizer
const events = ['DOMContentLoaded', 'shopify:section:load'];
events.forEach((event) => {
  document.addEventListener(event, async () => {
    initializeJS();
  });
});
